import api from "./api";

// ID card printing (system-user / RBAC — gated by the "view_id_card_print_requests",
// "print_id_cards" and "mark_id_card_issued" permissions on the backend).
//
// Unlike the /api/system/* endpoints, these general /api/* routes return plain,
// unwrapped JSON (verified directly against the backend) — no unwrap() needed.

// GET /api/id-card-print-requests — members whose ID card is pending print
// (card_status "Pending Print"). Optional { search, member_type_id, club_id }
// filters. Returns a plain array of Member objects: {id, title, initials,
// name_denoted_by_initials, lastname, member_gender, email, member_type,
// member_status, payment_status, ...}.
export const listIdCardPrintRequests = (params = {}) =>
  api
    .get("/api/id-card-print-requests", { params })
    .then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/members/{id} — full member details for the "View" action. The
// backend wraps its single result in an outer array ([[ … ]]), same as
// memberService.getMember — unwrap it here so callers get a plain object.
export const getMemberDetails = (id) =>
  api.get(`/api/members/${id}`).then((r) => (Array.isArray(r.data) ? r.data[0] : r.data));

// GET /api/id-card-print-requests/{memberId}/pdf — server-rendered ID card PDF
// (photo, QR code, member/club code baked in via DomPDF). Only members whose
// card_status is currently "Pending Print" are accepted — the backend 422s
// otherwise. responseType "blob" so axios doesn't try to JSON-parse the PDF.
export const getIdCardPdf = (memberId) =>
  api
    .get(`/api/id-card-print-requests/${memberId}/pdf`, { responseType: "blob" })
    .then((r) => r.data);

// POST /api/id-card-print-requests/{memberId}/mark-issued — flips the member's
// card_status from "Printed" to "Issued" (the backend 422s unless the member
// is currently "Printed"). Called after a successful print. Optional { note }
// string (max 500 chars) — no current caller sends one.
export const markIdCardIssued = (memberId, note) =>
  api.post(`/api/id-card-print-requests/${memberId}/mark-issued`, note ? { note } : {}).then((r) => r.data);

// GET /api/id-card-issue-requests — members whose card has been printed and is
// awaiting handover (card_status "Printed"). Same filters/response shape as
// listIdCardPrintRequests above, just scoped to the next queue stage.
export const listIdCardIssueRequests = (params = {}) =>
  api
    .get("/api/id-card-issue-requests", { params })
    .then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/id-card-preview/{memberId}/pdf — same server-rendered PDF as
// getIdCardPdf, but read-only: no card_status check and no status mutation,
// so it's safe to call regardless of the member's current queue stage.
export const getIdCardPreviewPdf = (memberId) =>
  api
    .get(`/api/id-card-preview/${memberId}/pdf`, { responseType: "blob" })
    .then((r) => r.data);

// GET /api/member-types — category list, used for the type/category filter.
export const listMemberTypes = () =>
  api.get("/api/member-types").then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/id-card-print-logs — global, server-paginated audit trail over
// every "printed"/"issued" id_card_print_logs row (who printed/issued which
// member's card, and when) — for staff troubleshooting, separate from the
// two live queues above. Optional { action: "printed"|"issued", search,
// date_from, date_to, printed_by_user_id, page, per_page } filters, all
// applied server-side. Unlike the queue endpoints, the response is a plain
// object (not a bare array): { filters, summary: {total, printed_count,
// issued_count}, page, per_page, total, logs: [...] } — return it as-is,
// same shape bookingLogService.js's listBookingLogs() callers expect.
export const listIdCardPrintLogs = (params = {}) =>
  api.get("/api/id-card-print-logs", { params }).then((r) => r.data);
